import React, { useState, useCallback, SyntheticEvent } from "react";
import { useSnackbar } from "@webiny/app-admin/hooks/useSnackbar";
import { Input } from "@webiny/ui/Input";
import { Tooltip } from "@webiny/ui/Tooltip";
import { Typography } from "@webiny/ui/Typography";
import {
    PageMeta,
    PageTitle,
    pageTitleWrapper,
    PageVersion,
    TitleInputWrapper,
    TitleWrapper
} from "./Styled";
import { useEventActionHandler } from "~/editor/hooks/useEventActionHandler";
import { usePage } from "~/pageEditor/hooks/usePage";
import { PageAtomType } from "~/pageEditor/state";
import { UpdateDocumentActionEvent } from "~/editor/recoil/actions";

declare global {
    interface Window {
        Cypress: any;
    }
}

interface PageInfo {
    pageTitle: string;
    pageVersion: number;
    pageLocked: boolean;
}

const extractPageInfo = (page: PageAtomType): PageInfo => {
    const { title, version, locked } = page;
    return {
        pageTitle: title as string,
        pageVersion: version,
        pageLocked: locked
    };
};

export const Title = () => {
    const handler = useEventActionHandler();
    const [page] = usePage();
    const { showSnackbar } = useSnackbar();
    const { pageTitle, pageVersion, pageLocked } = extractPageInfo(page);
    const [editTitle, setEdit] = useState<boolean>(false);
    const [stateTitle, setTitle] = useState<string | null>(null);
    let title = stateTitle === null ? pageTitle : stateTitle;

    const updatePage = (data: Partial<PageAtomType>) => {
        handler.trigger(
            new UpdateDocumentActionEvent({
                history: false,
                document: data,
                onFinish: () => {
                    showSnackbar(`Page title updated successfully!`);
                }
            })
        );
    };

    const enableEdit = useCallback(() => setEdit(true), []);

    const onBlur = useCallback(() => {
        if (title === "") {
            title = "Untitled";
            setTitle(title);
        }
        setEdit(false);
        updatePage({ title });
    }, [title]);

    const onKeyDown = useCallback(
        (e: SyntheticEvent<HTMLInputElement>) => {
            // @ts-expect-error
            switch (e.key) {
                case "Escape":
                    e.preventDefault();
                    setEdit(false);
                    setTitle(pageTitle);
                    break;
                case "Enter":
                    let title = e.currentTarget.value;
                    if (!title) {
                        title = "Untitled";
                        setTitle(title);
                    }

                    e.preventDefault();
                    setEdit(false);

                    updatePage({ title: title });
                    break;
                default:
                    return;
            }
        },
        [title, pageTitle]
    );

    // Disable autoFocus because for some reason, blur event would automatically be triggered when clicking
    // on the page title when doing Cypress testing. Not sure if this is RMWC or Cypress related issue.
    const autoFocus = !window.Cypress;

    return editTitle ? (
        <TitleInputWrapper data-testid="pb-editor-page-title">
            <Input
                autoFocus={autoFocus}
                fullwidth
                value={title}
                onChange={setTitle}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
            />
        </TitleInputWrapper>
    ) : (
        <TitleWrapper>
            <PageMeta>
                <Typography use={"overline"}>
                    {`(status: ${pageLocked ? "published" : "draft"})`}
                </Typography>
            </PageMeta>
            <div style={{ width: "100%", display: "flex" }}>
                <Tooltip
                    className={pageTitleWrapper}
                    placement={"bottom"}
                    content={<span>Rename</span>}
                >
                    <PageTitle data-testid="pb-editor-page-title" onClick={enableEdit}>
                        {title}
                    </PageTitle>
                </Tooltip>
                <PageVersion>{`(v${pageVersion})`}</PageVersion>
            </div>
        </TitleWrapper>
    );
};
